import { useCallback, useEffect, useRef } from 'react';

import { FormattedMessage } from 'react-intl';

import classNames from 'classnames';
import { useLocation } from 'react-router';

import {
  BellIcon,
  ChatCircleDotsIcon,
  HouseIcon,
  InfoIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react';
import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';

import {
  closeNavigation,
  openNavigation,
} from '@/flavours/glitch/actions/navigation';
import { Avatar } from '@/flavours/glitch/components/avatar';
import {
  Button,
  IconButton,
} from '@/flavours/glitch/components/button/redesign';
import { Menu, MenuList, MenuTrigger } from '@/flavours/glitch/components/menu';
import { FOCUS_TARGET } from '@/flavours/glitch/components/navigation_focus_target';
import { ComposeButton } from '@/flavours/glitch/features/compose/trigger';
import { useAccount } from '@/flavours/glitch/hooks/useAccount';
import { useIdentity } from '@/flavours/glitch/identity_context';
import {
  registrationsOpen,
  sso_redirect,
  trendsEnabled,
} from '@/flavours/glitch/initial_state';
import { useAppDispatch, useAppSelector } from '@/flavours/glitch/store';

import {
  RedesignNavigationPanel,
  useNotificationsCount,
  handleSearchLinkClick,
} from '.';
import { AccountMenuItems } from './account_card_and_menu';
import { LogoLockup } from './header';
import classes from './mobile_nav.module.scss';
import { MobileNavLink, MobileNavProfileButton } from './navigation_link';

export const RedesignMobileNavigation: React.FC = () => {
  const dispatch = useAppDispatch();
  const { accountId, signedIn } = useIdentity();
  const account = useAccount(accountId);

  const notificationsCount = useNotificationsCount();

  const openMobileNav = useCallback(() => {
    dispatch(openNavigation());
  }, [dispatch]);

  // Hide the mobile sign-up button if registrations are closed or
  // SSO is used (as there's not much room for a detailed label)
  const hasSignUpButton = !sso_redirect && !registrationsOpen;

  const signupUrl = useAppSelector(
    (state) => state.server.server.item?.registrations.url ?? '/auth/sign_up',
  );

  return (
    <>
      {signedIn ? (
        <nav className={classes.root}>
          <ul className={classNames(classes.floatingCard, classes.list)}>
            <MobileNavLink to='/home' iconComponent={HouseIcon}>
              <FormattedMessage id='tabs_bar.home' defaultMessage='Home' />
            </MobileNavLink>
            <MobileNavLink
              to={{
                pathname: trendsEnabled ? '/explore' : '/search',
                state: { focusTarget: FOCUS_TARGET.SEARCH },
              }}
              iconComponent={MagnifyingGlassIcon}
              onClick={handleSearchLinkClick}
            >
              <FormattedMessage id='tabs_bar.search' defaultMessage='Search' />
            </MobileNavLink>
            <MobileNavLink
              to='/conversations'
              iconComponent={ChatCircleDotsIcon}
            >
              <FormattedMessage
                id='tabs_bar.messages'
                defaultMessage='Messages'
                description='Message refers to a direct message. For languages where this is confusing, "chat" or "direct message" can be used.'
              />
            </MobileNavLink>
            <MobileNavLink
              to='/notifications'
              iconComponent={BellIcon}
              withDot={notificationsCount > 0}
            >
              <FormattedMessage
                id='tabs_bar.notifications'
                defaultMessage='Notifications'
              />
            </MobileNavLink>
            <Menu>
              <MenuTrigger
                as={MobileNavProfileButton}
                avatar={
                  <Avatar
                    size={24}
                    account={account}
                    className={classes.avatar}
                  />
                }
              >
                <FormattedMessage
                  id='tabs_bar.account_settings'
                  defaultMessage='Account settings'
                />
              </MenuTrigger>
              <MenuList placement='top-end' offset={8}>
                <AccountMenuItems context='mobile' />
              </MenuList>
            </Menu>
          </ul>
          <ComposeButton inline />
        </nav>
      ) : (
        // Logged out state:
        <nav className={classes.root}>
          <div
            className={classNames(classes.floatingCard, classes.loggedOutInfo)}
          >
            <LogoLockup className={classes.logoLockup} />
            {hasSignUpButton && (
              <Button
                as='a'
                href={signupUrl}
                variant='solid'
                color='accent'
                size='sm'
                className={classes.signUpButton}
              >
                <FormattedMessage
                  id='server_banner.sign_up'
                  defaultMessage='Sign up'
                />
              </Button>
            )}
            <IconButton size='sm' icon={InfoIcon} onClick={openMobileNav}>
              <FormattedMessage id='server_banner.info' defaultMessage='Info' />
            </IconButton>
          </div>
        </nav>
      )}

      <SlideOutNavigation />
    </>
  );
};

const MENU_WIDTH = 320;

const SlideOutNavigation: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const overlayRef = useRef<HTMLDivElement>(null);

  const isOpen = useAppSelector((state) => state.navigation.open);

  useEffect(() => {
    dispatch(closeNavigation());
  }, [dispatch, location]);

  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      if (overlayRef.current && e.target === overlayRef.current) {
        dispatch(closeNavigation());
      }
    };

    const handleDocumentKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dispatch(closeNavigation());
      }
    };

    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keyup', handleDocumentKeyUp);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('keyup', handleDocumentKeyUp);
    };
  }, [dispatch]);

  const isLtrDir = getComputedStyle(document.body).direction !== 'rtl';

  const OPEN_MENU_OFFSET = isLtrDir ? -MENU_WIDTH : MENU_WIDTH;

  const [{ x }, spring] = useSpring(
    () => ({
      x: isOpen ? 0 : OPEN_MENU_OFFSET,
      onRest: {
        x({ value }: { value: number }) {
          if (value === 0) {
            dispatch(openNavigation());
          } else if (value === OPEN_MENU_OFFSET) {
            dispatch(closeNavigation());
          }
        },
      },
    }),
    [isOpen],
  );

  const bind = useDrag(
    ({
      last,
      offset: [xOffset],
      velocity: [xVelocity],
      direction: [xDirection],
      cancel,
    }) => {
      const logicalXDirection = isLtrDir ? -xDirection : xDirection;
      const logicalXOffset = isLtrDir ? -xOffset : xOffset;
      const hasReachedDragThreshold = logicalXOffset < -70;

      if (hasReachedDragThreshold) {
        cancel();
      }

      if (last) {
        const isAboveOpenThreshold = logicalXOffset > MENU_WIDTH / 2;
        const isQuickFlick = xVelocity > 0.5 && logicalXDirection > 0;

        if (isAboveOpenThreshold || isQuickFlick) {
          void spring.start({ x: OPEN_MENU_OFFSET });
        } else {
          void spring.start({ x: 0 });
        }
      } else {
        void spring.start({ x: xOffset, immediate: true });
      }
    },
    {
      from: () => [x.get(), 0],
      axis: 'x',
      filterTaps: true,
      bounds: isLtrDir ? { right: 0 } : { left: 0 },
      rubberband: true,
    },
  );

  const previouslyFocusedElementRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (isOpen && overlay) {
      const firstLink = overlay.querySelector<HTMLAnchorElement>('a');
      previouslyFocusedElementRef.current =
        document.activeElement as HTMLElement;
      firstLink?.focus();
    } else {
      previouslyFocusedElementRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <div
      className={classes.slideOutWrapper}
      data-is-open={isOpen}
      ref={overlayRef}
      inert={!isOpen}
    >
      <animated.div className={classes.slideOut} {...bind()} style={{ x }}>
        <RedesignNavigationPanel mode='slide-out' />
      </animated.div>
    </div>
  );
};
